import { FUNCTION_TYPE, DATETIME_FORMAT_API } from '@/constants.js'
import moment from 'moment'

// ISOフォーマット
const ISO_FORMAT = DATETIME_FORMAT_API

/**
 * スケジュールのモデルクラス
 */
class Schedule {
  /**
   * コンストラクタ
   *
   * @param {Object} data スケジュールデータ
   * @param {Object} logger ロガー
   */
  constructor(data, logger) {
    this.id = (data && data.id) || null
    this.title = (data && data.title) || null
    this.detail = (data && data.detail) || null
    this.isAllDay = data && data.allday_flg
    // FIXME: startAt,endAt と creatAt,updateAt で保持形式が違う
    // モデルクラスとしては文字列で持つのが正しい気がするが
    // 文字列で持つとソートや比較でかなり頻繁に moment への変換が発生するので
    // 対応方法は要検討
    this.startAt = (data && moment(data.start_at)) || null
    this.endAt = (data && moment(data.end_at)) || null
    this.targetUsers = (data && data.target_users) || []
    this.targetOrganizations = data?.target_organizations || []
    this.extendTargetUsers = data?.extend_target_users || []
    this.assignedUsers =
      this.targetUsers.filter((user) => user.assign_flg) || []
    this.completeFlg = data && data.complete_flg
    this.endFlg = data && data.end_flg
    this.isContinueStart = (data && data.isContinueStart) || null
    this.isContinueEnd = (data && data.isContinueEnd) || null
    this.createAt = data && data.create_at
    this.createUserId = data && data.create_user_id
    this.createUserName = data && data.create_user_name
    this.updateAt = data && data.update_at
    this.updateUserId = data && data.update_user_id
    this.updateUserName = data && data.update_user_name
    this.originalStartAt = this.startAt
    this.originalEndAt = this.endAt
    this.checkinTask =
      (data &&
        data.tasks &&
        data.tasks.find(
          (task) => task.function_type == FUNCTION_TYPE.CHECKIN
        )) ||
      null
    this.reportTask =
      (data &&
        data.tasks &&
        data.tasks.find(
          (task) => task.function_type == FUNCTION_TYPE.REPORT
        )) ||
      null
    this.logger = logger
    this.scheduleAttachFiles = (data && data.schedule_attach_files) || []
    this.attributes = (data && data.attributes) || []
  }

  /**
   * 開始・終了ともに継続か？
   * @type {Boolean}
   */
  get isContinueBoth() {
    return this.isContinueStart && this.isContinueEnd
  }

  /**
   * タスク
   * @type {Array.<Object>}
   */
  get tasks() {
    const result = []
    this.checkinTask && result.push(this.checkinTask)
    this.reportTask && result.push(this.reportTask)
    return result
  }

  /**
   * プレイスID
   * @type {Number}
   */
  get placeId() {
    return !this.checkinTask ? null : this.checkinTask.parent_id
  }

  /**
   * プレイス名称
   * @type {String}
   */
  get placeName() {
    return !this.checkinTask ? null : this.checkinTask.parent_name
  }

  /**
   * チェックインID
   * @type {Number}
   */
  get checkinId() {
    return !this.checkinTask ? null : this.checkinTask.id
  }

  /**
   * チェックインは完了しているか？
   * @type {Booelan}
   */
  get isCheckinCompleted() {
    return !!this.checkinTask && this.checkinTask.complete_flg
  }

  /**
   * 報告書プロジェクトID
   * @type {Number}
   */
  get reportProjectId() {
    return !this.reportTask ? null : this.reportTask.parent_id
  }

  /**
   * 報告書プロジェクト名称
   * @type {String}
   */
  get reportProjectName() {
    return !this.reportTask ? null : this.reportTask.parent_name
  }

  /**
   * 報告書ID
   * @type {Number}
   */
  get reportId() {
    return !this.reportTask ? null : this.reportTask.id
  }

  /**
   * 報告書は完了しているか？
   * @type {Booelan}
   */
  get isReportCompleted() {
    return !!this.reportTask && this.reportTask.complete_flg
  }

  /**
   * 複数人タスク判定
   * @type {Boolean}
   */
  get hasMultiUserTask() {
    return this.hasMultiTargetUsers && (this.checkinTask || this.reportTask)
  }

  /**
   * targetUsers の ID
   * @type {number[]}
   */
  get targetUserIds() {
    return this.targetUsers.map((u) => u.user_id)
  }

  /**
   * targetOrganizations の ID
   * @type {number[]}
   */
  get targetOrganizationIds() {
    return this.targetOrganizations.map((o) => o.organization_id)
  }

  /**
   * 対象ユーザーが複数人存在するか？
   * @type {Boolean}
   */
  get hasMultiTargetUsers() {
    return this.extendTargetUsers.length > 1
  }

  /**
   * 対象ユーザーが複数人存在するか？
   * @type {Boolean}
   */
  get hasMultiAssignUsers() {
    return this.assignedUsers.length > 1
  }

  /**
   * 担当者が指定されているか？
   * @type {Boolean}
   */
  get hasAssignedUser() {
    return !!this.assignedUsers.length
  }

  /**
   * 担当者ユーザーID
   * @type {Array<Number>}
   */
  get assignedUserIds() {
    return this.assignedUsers.length
      ? this.assignedUsers.map((data) => data.user_id)
      : []
  }

  /**
   * スケジュールデータ
   * @returns {Object}
   */
  toJSON() {
    return {
      id: this.id,
      title: this.title,
      detail: this.detail,
      allday_flg: this.isAllDay,
      start_at: (this.startAt && this.startAt.format(ISO_FORMAT)) || null,
      end_at: (this.endAt && this.endAt.format(ISO_FORMAT)) || null,
      target_users: this.targetUsers,
      target_organizations: this.targetOrganizations,
      extend_target_users: this.extendTargetUsers,
      complete_flg: this.completeFlg,
      end_flg: this.endFlg,
      tasks: this.tasks,
      schedule_attach_files: this.scheduleAttachFiles,
      isContinueStart: this.isContinueStart,
      isContinueEnd: this.isContinueEnd,
      create_at: this.createAt,
      create_user_id: this.createUserId,
      create_user_name: this.createUserName,
      update_at: this.updateAt,
      update_user_id: this.updateUserId,
      update_user_name: this.updateUserName,
      attributes: this.attributes,
    }
  }

  /**
   * リクエスト用スケジュールデータ
   * @returns {Object}
   */
  toRequestJSON() {
    return {
      title: this.title,
      detail: this.detail,
      allday_flg: this.isAllDay,
      start_at:
        (this.originalStartAt && this.originalStartAt.format(ISO_FORMAT)) ||
        null,
      end_at:
        (this.originalEndAt && this.originalEndAt.format(ISO_FORMAT)) || null,
      target_users: this.targetUsers.map((u) => ({
        user_id: u.user_id,
        assign_flg: u.assign_flg,
        update_at: u.update_at,
      })),
      target_organizations: this.targetOrganizations.map((o) => ({
        organization_id: o.organization_id,
      })),
      complete_flg: this.completeFlg,
      schedule_attach_files: this.scheduleAttachFiles.map((file) => ({
        id: file.id,
      })),
      tasks: this.tasks,
      attributes: this.attributes.map((item) => ({
        attribute_id: item.attribute_id,
        attribute_value: item.attribute_value,
      })),
    }
  }

  /**
   * 指定日について切り出す
   * @param {String} date 指定日付
   * @returns {Object} 指定日におけるスケジュール
   */
  scoopAt(date) {
    const start = moment(date).startOf('day')
    const end = moment(date).endOf('day')

    // 重なりがない場合はnullを返却
    if (this.startAt.isAfter(end) || this.endAt.isBefore(start)) {
      return null
    }

    // 返却用オブジェクト初期化
    const ret = { ...this.toJSON() }
    ret.isContinueStart = ret.isContinueEnd = false

    // 継続判定
    if (this.startAt.isBefore(start)) {
      ret.start_at = start.format(ISO_FORMAT)
      ret.isContinueStart = true
    }
    if (this.endAt.isAfter(end)) {
      ret.end_at = end.format(ISO_FORMAT)
      ret.isContinueEnd = true
    }

    const result = new Schedule(ret, this.logger)

    // 元の開始終了を挿入
    result.originalStartAt = this.startAt
    result.originalEndAt = this.endAt

    return result
  }

  /**
   * 指定ユーザーが担当者に含まているか判定
   *
   * @param {Number} userId ユーザーID
   * @returns {Boolean} 担当者がuserIdのみの場合はtrue
   */
  isAssigned(userId) {
    return !!this.assignedUsers.find((data) => data.user_id === userId)
  }

  /**
   * 指定ユーザーが対象ユーザーに含まれる場合はtrue
   *
   * @param {Number} userId ユーザーID
   * @returns {Boolean} userIdが対象ユーザーに含まれている場合はtrue
   */
  isTargetUser(userId) {
    return !!this.extendTargetUsers.find((u) => u.user_id === +userId)
  }

  /**
   * 担当者がuserIdのみの場合はtrue
   *
   * @param {Number} userId ユーザーID
   * @returns {Boolean} 担当者がuserIdのみの場合はtrue
   */
  isTargetUserOnly(userId) {
    const targetUserIds = this.extendTargetUsers.map((user) => user.user_id)
    return targetUserIds.length === 1 && targetUserIds[0] === +userId
  }
}

export default Schedule
